import type { PaymentRunReviewRow } from '~entities/payment/api/reporting-payment-run-review';

export type PaymentRunReviewSortColumn =
  | 'dueDate'
  | 'parent'
  | 'student'
  | 'classSession'
  | 'billingType'
  | 'baseAmount'
  | 'discount'
  | 'finalAmount'
  | 'transactionFee'
  | 'paidDate'
  | 'paidAmount'
  | 'payment'
  | 'status';

function normStr(x: string | undefined): string {
  return (x ?? '').toLowerCase().trim();
}

function dueDateTimeMs(dueDate: string | undefined): number {
  if (dueDate == null || dueDate === '') return 0;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dueDate)
    ? `${dueDate}T12:00:00`
    : dueDate;
  return new Date(normalized).getTime();
}

export function comparePaymentRunReviewRows(
  a: PaymentRunReviewRow,
  b: PaymentRunReviewRow,
  column: PaymentRunReviewSortColumn,
  direction: 'asc' | 'desc',
): number {
  const mult = direction === 'asc' ? 1 : -1;
  let cmp = 0;
  switch (column) {
    case 'dueDate':
      cmp = dueDateTimeMs(a.dueDate) - dueDateTimeMs(b.dueDate);
      break;
    case 'parent':
      cmp = normStr(a.parent).localeCompare(normStr(b.parent));
      break;
    case 'student':
      cmp = normStr(a.student).localeCompare(normStr(b.student));
      break;
    case 'classSession': {
      const ca = [a.class, a.session].filter(Boolean).join(' / ');
      const cb = [b.class, b.session].filter(Boolean).join(' / ');
      cmp = normStr(ca).localeCompare(normStr(cb));
      break;
    }
    case 'billingType':
      cmp = normStr(a.billingType).localeCompare(normStr(b.billingType));
      break;
    case 'baseAmount':
      cmp = (Number(a.baseAmount) || 0) - (Number(b.baseAmount) || 0);
      break;
    case 'discount':
      cmp = (Number(a.discount) || 0) - (Number(b.discount) || 0);
      break;
    case 'finalAmount':
      cmp = (Number(a.finalAmount) || 0) - (Number(b.finalAmount) || 0);
      break;
    case 'transactionFee':
      cmp =
        (Number(a.transactionFee) || 0) - (Number(b.transactionFee) || 0);
      break;
    case 'paidDate':
      cmp = dueDateTimeMs(a.paidDate) - dueDateTimeMs(b.paidDate);
      break;
    case 'paidAmount':
      cmp = (Number(a.paidAmount) || 0) - (Number(b.paidAmount) || 0);
      break;
    case 'payment': {
      const pa = `${normStr(a.paymentMethod)}${a.cardLast4 ?? ''}`;
      const pb = `${normStr(b.paymentMethod)}${b.cardLast4 ?? ''}`;
      cmp = pa.localeCompare(pb);
      break;
    }
    case 'status':
      cmp = normStr(a.status).localeCompare(normStr(b.status));
      break;
    default:
      return 0;
  }
  if (cmp !== 0) return mult * cmp;
  return mult * normStr(a.id).localeCompare(normStr(b.id));
}

export function sortPaymentRunReviewRows(
  rows: Array<PaymentRunReviewRow>,
  column: PaymentRunReviewSortColumn,
  direction: 'asc' | 'desc',
): Array<PaymentRunReviewRow> {
  return [...rows].sort((a, b) =>
    comparePaymentRunReviewRows(a, b, column, direction),
  );
}
